import cs from '@shopify/polaris/locales/cs.json';
import da from '@shopify/polaris/locales/da.json';
import en from '@shopify/polaris/locales/en.json';
import fi from '@shopify/polaris/locales/fi.json';
import nb from '@shopify/polaris/locales/nb.json';
import pl from '@shopify/polaris/locales/pl.json';
import hu from './hu.json';
import ro from './ro.json';
import sk from './sk.json';
import sw from './sw.json';

export interface TranslationDictionary {
    [key: string]: string | TranslationDictionary;
}

/**
 * Polaris TranslationDictionary constant containing PickupBird equivalent languages. Not to be confused with AppLanguage.
 *
 * Consumed both by `usePolarisLanguage` (fed to Polaris' own `AppProvider i18n`) and by
 * `react-i18next-init` (registered as the `polaris` i18next namespace).
 *
 * @returns {TranslationDictionary} Shopify Polaris translation dictionaries.
 *  */
export const POLARIS_LANGUAGES: { [key: string]: TranslationDictionary } = {
    en,
    hu,
    cs,
    da,
    fi,
    nb,
    pl,
    ro,
    sk,
    sw,
};

/**
 * Polaris ships its locale files with single-brace placeholders (`"{selectedItemsCount} selected"`),
 * which Polaris' own i18n resolves internally. i18next interpolates double braces instead, so the
 * dictionaries have to be rewritten before they can be used as an i18next resource bundle.
 *  */
function toI18nextInterpolation<T>(value: T): T {
    if (typeof value === 'string') {
        return value.replace(/{(\w+)}/g, '{{$1}}') as unknown as T;
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value).map(([key, nested]) => [key, toI18nextInterpolation(nested)])
        ) as T;
    }

    return value;
}

/**
 * The `POLARIS_LANGUAGES` dictionaries shaped as i18next resources, so `useTranslation('polaris')`
 * can resolve keys like `Polaris.IndexProvider.selected`.
 *  */
export const POLARIS_I18NEXT_RESOURCES = Object.fromEntries(
    Object.entries(POLARIS_LANGUAGES).map(([language, dictionary]) => [
        language,
        { polaris: toI18nextInterpolation(dictionary) },
    ])
);
